import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import './CustomSelect.css'

/**
 * Универсальный кастомный Select
 * 
 * @param {Array} options - [{ value, label, icon?, badge?, group? }]
 * @param {string} value - текущее выбранное значение
 * @param {function} onChange - callback(value)
 * @param {string} placeholder - текст когда ничего не выбрано
 * @param {string} label - заголовок над селектом
 * @param {string} className - доп. классы
 * @param {boolean} disabled
 */
export default function CustomSelect({ 
  options = [], 
  value, 
  onChange, 
  placeholder = 'Выберите...', 
  label,
  className = '',
  disabled = false
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [dropStyle, setDropStyle] = useState({})
  const ref = useRef(null)
  const searchRef = useRef(null)

  // Рассчитываем позицию dropdown при открытии
  const calcPosition = useCallback(() => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const dropHeight = Math.min(options.length * 44 + (options.length > 5 ? 48 : 0) + 16, 310)
    const openAbove = spaceBelow < dropHeight && spaceAbove > spaceBelow

    setDropStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 10001,
    })
  }, [options.length])

  // Закрытие при клике вне
  useEffect(() => {
    if (!open) return
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // Также проверяем dropdown (portal)
        const portal = document.querySelector('.cselect-portal')
        if (portal && portal.contains(e.target)) return
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  // Закрытие по Escape
  useEffect(() => {
    if (!open) return
    const handle = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [open])

  // Фокус на поиск при открытии (если > 5 опций) + пересчёт позиции при скролле/resize
  useEffect(() => {
    if (open && options.length > 5 && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50)
    }
    if (!open) { setSearch(''); return }

    calcPosition()
    const handleScroll = () => calcPosition()
    const handleResize = () => calcPosition()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [open, options.length, calcPosition])

  // Сравнение с приведением типов (number vs string)
  const selected = options.find(o => String(o.value) === String(value))
  const displayText = selected?.label || placeholder

  // Фильтруем по поиску
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options

  // Группировка
  const groups = {}
  filtered.forEach(opt => {
    const g = opt.group || '__default'
    if (!groups[g]) groups[g] = []
    groups[g].push(opt)
  })

  const handleSelect = (val) => {
    onChange(val)
    setOpen(false)
  }

  const handleToggle = () => {
    if (disabled) return
    if (!open) calcPosition()
    setOpen(!open)
  }

  const dropdown = open ? createPortal(
    <div className="cselect-portal" style={dropStyle}>
      <div className="cselect-dropdown">
        {options.length > 5 && (
          <div className="cselect-search-wrap">
            <input
              ref={searchRef}
              className="cselect-search"
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}
        <div className="cselect-options">
          {Object.entries(groups).map(([groupName, groupOptions]) => (
            <div key={groupName}>
              {groupName !== '__default' && (
                <div className="cselect-group-label">{groupName}</div>
              )}
              {groupOptions.map(opt => (
                <button
                  key={String(opt.value)}
                  className={`cselect-option ${String(value) === String(opt.value) ? 'cselect-option-active' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                  type="button"
                >
                  {opt.icon && <span className="cselect-option-icon">{opt.icon}</span>}
                  <span className="cselect-option-text">{opt.label}</span>
                  {opt.badge && <span className="cselect-option-badge">{opt.badge}</span>}
                  {String(value) === String(opt.value) && (
                    <span className="cselect-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="cselect-empty">Ничего не найдено</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  ) : null

  return (
    <div className={`cselect ${className} ${disabled ? 'cselect-disabled' : ''}`} ref={ref}>
      {label && <label className="cselect-label">{label}</label>}
      <button
        className={`cselect-trigger ${open ? 'cselect-trigger-open' : ''}`}
        onClick={handleToggle}
        type="button"
        disabled={disabled}
      >
        <span className="cselect-value">
          {selected?.icon && <span className="cselect-value-icon">{selected.icon}</span>}
          <span className={!selected ? 'cselect-placeholder' : ''}>{displayText}</span>
        </span>
        <span className={`cselect-chevron ${open ? 'cselect-chevron-open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      {dropdown}
    </div>
  )
}
